// -- AppFw Error types and handling --

interface AppFwErrorResponseRecord {
    readonly code: string;
    readonly message?: string;
    readonly parameters?: any;
}

function isAppFwErrorResponseRecord(obj: any): obj is AppFwErrorResponseRecord {
    return (
        typeof obj === "object" &&
        obj !== null &&
        typeof obj.code === "string"
    );
}

interface AppFwErrorResponse {
    readonly errors: AppFwErrorResponseRecord[];
}

function isAppFwErrorResponse(obj: any): obj is AppFwErrorResponse {
    return (
        typeof obj === "object" &&
        obj !== null &&
        Array.isArray(obj.errors) &&
        obj.errors.every(isAppFwErrorResponseRecord)
    );
}

export interface AppFwError {
    readonly errorRecords?: AppFwErrorResponseRecord[];
    readonly message: string;
}

function isAppFwError(obj: any): obj is AppFwError {
    return (
        typeof obj === "object" &&
        obj !== null &&
        typeof obj.message === "string" &&
        "errorRecords" in obj
    );
}

export async function handleError(e: unknown): Promise<AppFwError> {

    let message = "Unknown error";
    let response: AppFwErrorResponseRecord[] | undefined = undefined;

    if (isAppFwError(e)) {
        return e;
    } else if (e instanceof Response) {
        try {
            const data = await e.clone().json()
            if (isAppFwErrorResponse(data)) {
                response = data.errors;
                message = data.errors.map(err => err.message || err.code)
                    .filter(Boolean)
                    .join("; ") || message;
            } else {
                message = JSON.stringify(data);
            }
        } catch {
            message = await e.text();
        }
    } else if (isAppFwErrorResponse(e)) {
        response = e.errors;
        message = e.errors.map(err => err.message).filter(Boolean).join("; ") || message;
    } else if (e instanceof Error) {
        message = e.message;
    }

    const error: AppFwError = { errorRecords: response, message }
    console.warn(error)
    return error
}

async function parseAppFwError(response: Response): Promise<AppFwError> {
    return handleError(response);
}

// -- API types --

export interface ApiResponse {
    status: number
    error?: AppFwError
}

export interface ApiResponseResult<T> {
    data: T | null
    response: ApiResponse
}

export enum HttpMethod {
    DELETE = "DELETE",
    GET = "GET",
    PATCH = "PATCH",
    POST = "POST",
    PUT = "PUT",
}

export interface RequestOptions {
    abortController?: AbortController;
}

interface ApiFetchOptions extends RequestOptions {
    readonly searchParams?: { [key: string]: string };
    readonly params?: any;
    readonly sessionId?: null | undefined | string;
    readonly method?: HttpMethod;
    readonly pathIsUrl?: boolean;
}

export class ApiClient {

    constructor(public readonly baseUrl: string) {
    }

    async get<T>(path: string, opts?: RequestOptions): Promise<T> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<T>(
            path,
            {
                ...opts,
                method: HttpMethod.GET,
                sessionId: sessionId,
            },
        )
    }

    async post<T>(path: string, payload?: any, opts?: RequestOptions): Promise<T> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<T>(
            path,
            {
                ...opts,
                method: HttpMethod.POST,
                sessionId: sessionId,
                params: payload,
            },
        )
    }

    async put<T>(path: string, payload?: any, opts?: RequestOptions): Promise<T> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<T>(
            path,
            {
                ...opts,
                method: HttpMethod.PUT,
                sessionId: sessionId,
                params: payload,
            },
        )
    }

    async delete<T>(path: string, opts?: RequestOptions): Promise<T> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<T>(
            path,
            {
                ...opts,
                method: HttpMethod.DELETE,
                sessionId: sessionId,
            },
        )
    }

    async fetch<T>(path: string, options?: ApiFetchOptions): Promise<T> {
        const result = await this.fetchWithResponse<T>(path, options);
        if (result.response.error) {
            throw result.response.error;
        }
        return result.data as T;
    }

    async fetchWithResponse<T>(path: string, options?: ApiFetchOptions): Promise<ApiResponseResult<T>> {
        const method = options ? (options.method || HttpMethod.POST) : HttpMethod.POST
        let headers = new Headers({
            'Accept': 'application/json',
            "X-Client-Version": __APP_VERSION__,
        })
        if (options && options.sessionId) {
            headers.set('Authorization', `Session ${options.sessionId}`)
        }
        if (method === HttpMethod.PATCH || method === HttpMethod.POST || method === HttpMethod.PUT) {
            headers.set('Content-Type', 'application/json')
        }

        let url
        if (options?.pathIsUrl) {
            url = path
        } else {
            url = `${this.baseUrl}${path}`
        }

        if (options?.searchParams) {
            let first = !url.includes('?')
            for (const [key, value] of Object.entries(options.searchParams)) {
                if (first) {
                    url += `?${key}=${value}`
                    first = false
                } else {
                    url += `&${key}=${value}`
                }
            }
        }

        let response = await fetch(`${url}`, {
            headers: headers,
            method,
            body: options ? JSON.stringify(options.params) : '',
        });

        // (TODO: still needed? => no, because of the improved logout logic)
        // Special check for 'unauthorized' with sessionId
        // if (response.status === 401 && options && options.sessionId) {
        //     window.location.href = window.location.origin
        // }

        if (!response.ok) {
            const error = await parseAppFwError(response)
            return { data: null, response: { status: response.status, error } }
        }

        const body = await response.text();
        let data: T | null = null
        if (body && body.length > 0) {
            data = JSON.parse(body);
        }

        return { data, response: { status: response.status } }
    }

}
